import { motion } from "framer-motion";
import { Lock, Eye, MapPin, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { FORT_AGENT_STATUS, MyFortAgentItem } from "@/api/types";
import { showInfo } from "@/lib/toast";
import { getAgentTierKey, getTokenName } from "@/config/common";
import { C } from "@/components/modal/home";
import { useMyAgentsUiStore } from "@/store/useMyAgentsUiStore";
import { AGENT_IMAGE_URL } from "./constants";
import {
  formatEtherWithDecimals,
  formatThousands,
  formatUsdPrefix,
} from "@/lib/utils";
import { CCC_AIF_ADDRESS } from "@/config/env";

export function HomeMyAgentSummaryCard({
  agent,
  pendingCount,
  onAddress,
  onGoManage,
}: {
  agent: MyFortAgentItem;
  pendingCount?: number;
  onAddress: () => void;
  onGoManage: () => void;
}) {
  const { t } = useTranslation();
  const { openActiveDetail } = useMyAgentsUiStore();
  const isActivated = agent.status === FORT_AGENT_STATUS.ACTIVATED;
  const tierLabel = t(getAgentTierKey(agent.name));
  const pendingBadge =
    !isActivated && pendingCount && pendingCount > 1 ? `x${pendingCount}` : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl overflow-hidden relative"
      style={{
        background: isActivated
          ? "linear-gradient(135deg, rgba(34,197,94,0.015) 0%, #0c1020 40%, #080c18 100%)"
          : "linear-gradient(135deg, rgba(212,165,116,0.015) 0%, #0c1020 40%, #080c18 100%)",
        border: isActivated
          ? "1px solid rgba(34,197,94,0.07)"
          : "1px solid rgba(212,165,116,0.07)",
        boxShadow: isActivated
          ? "0 2px 20px rgba(34,197,94,0.02)"
          : "0 2px 20px rgba(212,165,116,0.02)",
      }}
    >
      <div
        className="h-px"
        style={{
          background: isActivated
            ? "linear-gradient(90deg, transparent 10%, rgba(34,197,94,0.08) 50%, transparent 90%)"
            : "linear-gradient(90deg, transparent 10%, rgba(212,165,116,0.08) 50%, transparent 90%)",
        }}
      />
      <div className="flex items-stretch" style={{ minHeight: 0 }}>
        <div
          className="relative flex flex-col items-center justify-center overflow-hidden"
          style={{
            width: "42%",
            backgroundColor: "#0a0e1a",
            backgroundImage: isActivated
              ? "radial-gradient(ellipse at center 60%, rgba(147,51,234,0.12) 0%, rgba(34,197,94,0.08) 35%, #0a0e1a 70%)"
              : "radial-gradient(ellipse at center 60%, rgba(100,116,139,0.08) 0%, #0a0e1a 60%)",
          }}
        >
          <div className="absolute inset-0 pointer-events-none">
            {isActivated ? (
              <>
                <div
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full blur-[50px] glow-breathe"
                  style={{ background: "rgba(147,51,234,0.15)" }}
                />
                <div
                  className="absolute top-1/3 right-1/4 w-12 h-12 rounded-full blur-[25px] glow-breathe"
                  style={{
                    background: "rgba(168,85,247,0.10)",
                    animationDelay: "1.5s",
                  }}
                />
                <div
                  className="absolute bottom-1/4 left-1/4 w-10 h-10 rounded-full blur-[20px] glow-breathe"
                  style={{
                    background: "rgba(34,197,94,0.08)",
                    animationDelay: "2.5s",
                  }}
                />
              </>
            ) : (
              <div
                className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full blur-[50px]"
                style={{ background: "rgba(100,140,200,0.08)" }}
              />
            )}
          </div>
          <div
            className={`absolute inset-0 z-10 flex items-center justify-center p-2.5${isActivated ? " agent-float" : ""}`}
          >
            <img
              src={AGENT_IMAGE_URL}
              alt="AI Agent"
              className="h-full w-auto object-contain max-w-[90%]"
              style={{
                filter: isActivated
                  ? "drop-shadow(0 0 14px rgba(147,51,234,0.3)) drop-shadow(0 0 24px rgba(34,197,94,0.14))"
                  : "grayscale(100%) brightness(0.7)",
                opacity: isActivated ? 1 : 0.38,
              }}
            />
            {!isActivated && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(0,0,0,0.45)",
                    border: "1px solid rgba(212,165,116,0.20)",
                  }}
                >
                  <Lock size={14} style={{ color: "#d4a574" }} />
                </div>
              </div>
            )}
          </div>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-1">
            <div
              className={`w-full h-full rounded-full${isActivated ? " energy-pulse" : ""}`}
              style={{
                background: isActivated
                  ? "radial-gradient(ellipse, rgba(147,51,234,0.4) 0%, rgba(168,85,247,0.18) 40%, transparent 70%)"
                  : "radial-gradient(ellipse, rgba(100,116,139,0.12) 0%, transparent 70%)",
              }}
            />
          </div>
        </div>

        <div
          className="flex-1 py-3.5 pr-3.5 pl-1 space-y-2"
          style={{ minWidth: 0 }}
        >
          <div>
            {isActivated ? (
              <>
                {/* 已激活：与原先一致，单行 — 名称 + 等级 + 运行中（ml-auto） */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="font-black text-base tracking-wide"
                    style={{ color: C.text }}
                  >
                    {agent.name}
                  </span>
                  <span
                    className="text-[10px] leading-none px-1 py-px rounded font-semibold shrink-0"
                    style={{
                      background: `${C.amber}12`,
                      color: C.amber,
                      border: `1px solid ${C.amber}20`,
                    }}
                  >
                    {tierLabel}
                  </span>
                  <span
                    className="flex items-center gap-1 px-1.5 py-[2px] rounded text-[10px] font-bold ml-auto"
                    style={{
                      background: "rgba(34,197,94,0.06)",
                      color: C.green,
                      border: "1px solid rgba(34,197,94,0.10)",
                    }}
                  >
                    <div
                      className="w-1 h-1 rounded-full animate-pulse"
                      style={{ background: C.green }}
                    />
                    {t("homePage.myAgentCard.statusRunning")}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* 未激活：第一行名称 + 待激活（同已激活一行结构）；第二行仅等级 + 台数 */}
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="font-black text-base tracking-wide min-w-0 flex-1 truncate"
                    style={{ color: C.muted }}
                  >
                    {agent.name}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded text-[10px] font-bold shrink-0"
                    style={{
                      background: "rgba(212,165,116,0.06)",
                      color: C.amber,
                      border: "1px solid rgba(212,165,116,0.10)",
                    }}
                  >
                    <Lock size={9} className="shrink-0" />
                    {t("homePage.myAgentCard.statusPending")}
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                  <span
                    className="text-[10px] leading-none px-1 py-px rounded font-semibold shrink-0"
                    style={{
                      background: `${C.amber}12`,
                      color: C.amber,
                      border: `1px solid ${C.amber}20`,
                    }}
                  >
                    {tierLabel}
                  </span>
                  {!!pendingBadge && (
                    <span
                      className="text-[10px] leading-none px-1.5 py-px rounded font-black shrink-0"
                      style={{
                        color: C.green,
                        background: `${C.green}12`,
                        border: `1px solid ${C.green}30`,
                      }}
                    >
                      {pendingBadge}
                    </span>
                  )}
                </div>
              </>
            )}
            <div className="mt-1 text-xs" style={{ color: C.dim }}>
              {isActivated
                ? t("homePage.myAgentCard.activatedAt", {
                    date: dayjs(agent.activatedAt).format("YYYY-MM-DD"),
                  })
                : ""}
              {/* t("homePage.myAgentCard.purchasedNotActivated", {
                    date: dayjs(agent.createdAt).format("YYYY-MM-DD"),
                  }) */}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <div
              className="text-center py-1.5 rounded-lg basis-[calc(25%-0.375rem)] min-w-[58px] flex-1"
              style={{
                background: "rgba(12,16,32,0.5)",
                border: "1px solid rgba(100,140,200,0.04)",
              }}
            >
              <div className="text-xs mb-0.5" style={{ color: C.dim }}>
                {t("homePage.benefit.custodyBoost")}
              </div>
              <div
                className="text-xs font-bold mono-num"
                style={{ color: C.primary }}
              >
                {`${agent.aiManageAddSpeedBp / 100}%`}
              </div>
            </div>
            <div
              className="text-center py-1.5 rounded-lg basis-[calc(25%-0.375rem)] min-w-[58px] flex-1"
              style={{
                background: "rgba(12,16,32,0.5)",
                border: "1px solid rgba(100,140,200,0.04)",
              }}
            >
              <div className="text-xs mb-0.5" style={{ color: C.dim }}>
                {t("homePage.benefit.releaseMultiple")}
              </div>
              <div
                className="text-xs font-bold mono-num"
                style={{ color: C.green }}
              >
                {`${agent.exitMultiple / 100}x`}
              </div>
            </div>
            <div
              className="text-center py-1.5 rounded-lg basis-[calc(25%-0.375rem)] min-w-[58px] flex-1"
              style={{
                background: "rgba(12,16,32,0.5)",
                border: "1px solid rgba(100,140,200,0.04)",
              }}
            >
              <div className="text-xs mb-0.5" style={{ color: C.dim }}>
                {t("homePage.benefit.aifToken", {
                  tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
                })}
              </div>
              <div
                className="text-xs font-bold mono-num"
                style={{ color: C.amber }}
              >
                {formatUsdPrefix(
                  formatThousands(
                    formatEtherWithDecimals(agent.aifValueUsdt, 0),
                    0
                  )
                )}
              </div>
            </div>
            {agent.giftGiveLevel > 0 ? (
              <div
                className="text-center py-1.5 rounded-lg basis-[calc(25%-0.375rem)] min-w-[58px] flex-1"
                style={{
                  background: "rgba(12,16,32,0.5)",
                  border: "1px solid rgba(100,140,200,0.04)",
                }}
              >
                <div className="text-xs mb-0.5" style={{ color: C.dim }}>
                  {t("homePage.benefit.teamLevel")}
                </div>
                <div
                  className="text-xs font-bold mono-num"
                  style={{ color: C.cyan }}
                >
                  {`C${agent.giftGiveLevel}`}
                </div>
              </div>
            ) : null}
          </div>

          {isActivated && (
            <div className="grid grid-cols-3 gap-1.5">
              {(
                [
                  {
                    icon: <MapPin size={10} />,
                    label: t("homePage.myAgentCard.deliveryAddress"),
                    onClick: onAddress,
                  },
                  {
                    icon: <Truck size={10} />,
                    label: t("homePage.myAgentCard.deliveryStatus"),
                    onClick: () =>
                      showInfo(t("homePage.myAgentCard.deliveryStatusToast")),
                  },
                  {
                    icon: <Eye size={10} />,
                    label: t("homePage.myAgentCard.viewDetail"),
                    onClick: () => openActiveDetail(agent.agentId),
                  },
                ] as const
              ).map(item => (
                <button
                  key={item.label}
                  type="button"
                  className="flex flex-col items-center gap-1 py-1.5 rounded-lg text-xs font-medium transition-all active:scale-95"
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    border: `1px solid ${C.border}`,
                    color: C.muted,
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    item.onClick();
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {!isActivated && (
            <button
              type="button"
              className="w-full py-2 rounded-lg text-xs font-bold transition-all active:scale-95"
              style={{
                background: `${C.cyan}12`,
                border: `1px solid ${C.cyan}35`,
                color: C.cyan,
              }}
              onClick={onGoManage}
            >
              {t("homePage.myAgents.view")}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
